import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RecommendationCard } from "./RecommendationCard";
import { RecommendationsKanban } from "./RecommendationsKanban";
import { GoDeeperDialog } from "./GoDeeperDialog";
import { CompleteRecommendationDialog } from "./CompleteRecommendationDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Recommendation, ArtieScoreData } from "@/types/artie-score";
import { apiClient } from "@/lib/api";
import { Sparkles, CheckCircle2, Clock, XCircle, LayoutGrid, List, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCollaboration } from "@/hooks/use-collaboration";
import { useAuthStore } from "@/stores/authStore";

interface RecommendationsSectionProps {
  scoreData: ArtieScoreData;
  projectId: string;
  onUpdate: () => void;
}

export const RecommendationsSection = ({
  scoreData,
  projectId,
  onUpdate,
}: RecommendationsSectionProps) => {
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
  const [goDeeperOpen, setGoDeeperOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban');
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Collaboration hook
  const {
    stickyNotes,
    canComment,
    canEdit,
    isOwner,
    addStickyNote,
    updateStickyNote,
    deleteStickyNote,
  } = useCollaboration(projectId, user?._id || "");

  const recommendations = scoreData.recommendations || [];

  // Mark new recommendations as viewed when they are first displayed
  useEffect(() => {
    const markNewRecommendationsAsViewed = async () => {
      const newRecommendations = recommendations.filter(rec => rec.is_new);
      if (newRecommendations.length > 0) {
        try {
          const recIds = newRecommendations.map(rec => rec.id || rec.what_to_improve);
          
          // Separate V2 tactics from legacy recommendations
          const v2Tactics = newRecommendations.filter(rec => 'tactic_id' in rec);
          const legacyRecs = newRecommendations.filter(rec => !('tactic_id' in rec));
          
          // Mark V2 tactics as viewed
          if (v2Tactics.length > 0) {
            const v2TacticIds = v2Tactics.map(rec => rec.id || rec.what_to_improve);
            await apiClient.markTacticsAsViewed(projectId, v2TacticIds);
          }
          
          // Mark legacy recommendations as viewed
          if (legacyRecs.length > 0) {
            const legacyRecIds = legacyRecs.map(rec => rec.id || rec.what_to_improve);
            await apiClient.markRecommendationsAsViewed(projectId, legacyRecIds);
          }
          
          // Trigger update to refresh the data
          onUpdate();
        } catch (error) {
          console.error('Failed to mark recommendations as viewed:', error);
        }
      }
    };

    markNewRecommendationsAsViewed();
  }, [recommendations, projectId, onUpdate]);

  // Group recommendations by status
  const allActiveRecs = recommendations.filter(r =>
    !r.status || r.status === 'generated' || r.status === 'presented'
  );
  const acceptedRecs = recommendations.filter(r =>
    r.status === 'accepted' || r.status === 'in_progress'
  );
  const completedRecs = recommendations.filter(r => r.status === 'done');
  const declinedRecs = recommendations.filter(r => r.status === 'declined');

  // Always limit active recommendations to exactly 3 (sorted by priority)
  // Active includes: generated, presented, accepted, in_progress (but limit total to 3)
  const allActiveAndInProgress = recommendations.filter(r =>
    !r.status || r.status === 'generated' || r.status === 'presented' ||
    r.status === 'accepted' || r.status === 'in_progress'
  );
  const activeRecs = allActiveAndInProgress
    .sort((a, b) => (a.priority || 0) - (b.priority || 0))
    .slice(0, 3);

  const handleAccept = async (recId: string) => {
    try {
      await apiClient.updateRecommendation(projectId, {
        recommendation_id: recId,
        status: 'accepted',
      });
      toast({
        title: "Recommendation Accepted",
        description: "You can now use 'Go Deeper' to explore this recommendation.",
      });
      onUpdate();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to accept recommendation",
        variant: "destructive",
      });
    }
  };

  const handleDecline = async (recId: string) => {
    try {
      await apiClient.updateRecommendation(projectId, {
        recommendation_id: recId,
        status: 'declined',
      });
      toast({
        title: "Recommendation Declined",
        description: "You can reconsider this later if needed.",
      });
      onUpdate();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to decline recommendation",
        variant: "destructive",
      });
    }
  };

  const handleGoDeeper = (recId: string) => {
    const rec = recommendations.find(r => 
      (r.id || r.what_to_improve) === recId
    );
    if (rec) {
      setSelectedRecommendation(rec);
      setGoDeeperOpen(true);
    }
  };

  const handleComplete = (recId: string) => {
    const rec = recommendations.find(r => 
      (r.id || r.what_to_improve) === recId
    );
    if (rec) {
      setSelectedRecommendation(rec);
      setCompleteDialogOpen(true);
    }
  };

  const handleRecalculate = async () => {
    try {
      setIsRecalculating(true);
      
      toast({
        title: "Starting Recalculation",
        description: "Triggering full Artie Score recalculation...",
      });
      
      // Trigger recalculation (uses Celery async processing)
      const response = await apiClient.recalculateArtieScore(projectId) as any;
      console.log("Recalculate response:", response);
      
      // Check if we got async processing response
      if (response.status === "processing") {
        // Start polling for completion - keep isRecalculating true during polling
        const interval = setInterval(async () => {
          try {
            const statusResponse = await apiClient.getArtieScoreStatus(projectId) as any;
            console.log("Status response:", statusResponse);
            
            if (statusResponse.status === "completed") {
              // Calculation completed
              clearInterval(interval);
              setPollingInterval(null);
              setIsRecalculating(false); // Only set false when actually complete
              
              toast({
                title: "Recalculation Complete",
                description: "Your Artie Score has been updated!",
              });
              
              // Navigate to the Artie Score page to show results
              navigate(`/projects/${projectId}/artie-score`);
            } else if (statusResponse.status === "failed") {
              // Calculation failed
              clearInterval(interval);
              setPollingInterval(null);
              setIsRecalculating(false);
              
              toast({
                title: "Recalculation Failed",
                description: statusResponse.error || "Failed to recalculate score",
                variant: "destructive",
              });
            }
            // If still processing, keep polling and keep isRecalculating true
          } catch (pollError: any) {
            console.error("Polling error:", pollError);
            clearInterval(interval);
            setPollingInterval(null);
            setIsRecalculating(false);
            
            toast({
              title: "Error",
              description: "Failed to check recalculation status",
              variant: "destructive",
            });
          }
        }, 3000); // Poll every 3 seconds
        
        setPollingInterval(interval);
        // Keep isRecalculating true - it will be set false when polling completes
      } else if (response.status === "completed" && response.score_data) {
        // Immediate completion (sync fallback)
        setIsRecalculating(false);
        toast({
          title: "Score Updated",
          description: "Your Artie Score has been updated!",
        });
        navigate(`/projects/${projectId}/artie-score`);
      } else {
        // Unexpected response
        setIsRecalculating(false);
        toast({
          title: "Unexpected Response",
          description: "Please try again or refresh the page",
          variant: "destructive",
        });
      }
      
    } catch (error: any) {
      console.error("Recalculation error:", error);
      setIsRecalculating(false);
      toast({
        title: "Error",
        description: error.message || "Failed to recalculate score",
        variant: "destructive",
      });
    }
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Show loading screen during recalculation
  if (isRecalculating) {
    return (
      <div className="fixed inset-0 bg-slate-50 dark:bg-slate-900 z-50 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md px-4">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Recalculating Your Artie Score</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Performing full recalculation with latest data. This may take 2-3 minutes as we:
          </p>
          <ul className="text-sm text-slate-500 dark:text-slate-400 text-left space-y-2 mt-4">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Sync your latest YouTube channel data</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Reprocess your script content</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Recalculate reach, brand fit, and production readiness</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Generate updated recommendations</span>
            </li>
          </ul>
          <p className="text-xs text-slate-400 mt-4">
            Please wait while we process your updated score...
          </p>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="bg-[var(--card)] rounded-xl border border-[var(--border)] p-8 text-center">
        <Sparkles className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
        <p className="text-muted-foreground">No recommendations available yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Recalculate Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Recommendations</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Prioritized actions to improve your score
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-secondary rounded-lg p-1 border border-border">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={`h-7 px-2 ${viewMode === 'list' ? 'bg-background shadow-sm' : ''}`}
              title="List View"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className={`h-7 px-2 ${viewMode === 'kanban' ? 'bg-background shadow-sm' : ''}`}
              title="Kanban View"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>

          {completedRecs.length > 0 && (
            <Button
              onClick={handleRecalculate}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Recalculate Score
            </Button>
          )}
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <RecommendationsKanban
          recommendations={recommendations}
          projectId={projectId}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onGoDeeper={handleGoDeeper}
          onComplete={handleComplete}
          stickyNotes={stickyNotes}
          onAddStickyNote={addStickyNote}
          onUpdateStickyNote={updateStickyNote}
          onDeleteStickyNote={deleteStickyNote}
          canComment={canComment}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={user?._id || ""}
          readOnly={!canComment && !canEdit}
        />
      ) : (
        /* Tabs for different statuses */
        <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Active ({allActiveRecs.length})
          </TabsTrigger>
          <TabsTrigger value="in-progress" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            In Progress ({acceptedRecs.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Completed ({completedRecs.length})
          </TabsTrigger>
          <TabsTrigger value="declined" className="flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Declined ({declinedRecs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4 mt-6">
          {allActiveRecs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No active recommendations</p>
            </div>
          ) : (
            allActiveRecs.map((rec, idx) => (
              <RecommendationCard
                key={idx}
                recommendation={rec}
                projectId={projectId}
                onAccept={handleAccept}
                onDecline={handleDecline}
                onGoDeeper={handleGoDeeper}
                onComplete={handleComplete}
                stickyNotes={stickyNotes}
                onAddStickyNote={(content) => addStickyNote(rec.id || rec.what_to_improve, content)}
                onUpdateStickyNote={updateStickyNote}
                onDeleteStickyNote={deleteStickyNote}
                canComment={canComment}
                canEdit={canEdit}
                isOwner={isOwner}
                currentUserId={user?._id || ""}
                readOnly={!canComment && !canEdit}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="in-progress" className="space-y-4 mt-6">
          {acceptedRecs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No recommendations in progress</p>
            </div>
          ) : (
            acceptedRecs.map((rec, idx) => (
              <RecommendationCard
                key={idx}
                recommendation={rec}
                projectId={projectId}
                onAccept={handleAccept}
                onDecline={handleDecline}
                onGoDeeper={handleGoDeeper}
                onComplete={handleComplete}
                stickyNotes={stickyNotes}
                onAddStickyNote={(content) => addStickyNote(rec.id || rec.what_to_improve, content)}
                onUpdateStickyNote={updateStickyNote}
                onDeleteStickyNote={deleteStickyNote}
                canComment={canComment}
                canEdit={canEdit}
                isOwner={isOwner}
                currentUserId={user?._id || ""}
                readOnly={!canComment && !canEdit}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4 mt-6">
          {completedRecs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No completed recommendations yet</p>
              <p className="text-xs mt-2">Complete recommendations to see them here</p>
            </div>
          ) : (
            <>
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-600 dark:text-green-400">
                  <strong>Great progress!</strong> Click "Recalculate Score" above to see how these completions impact your Artie Score.
                </p>
              </div>
              {completedRecs.map((rec, idx) => (
                <RecommendationCard
                  key={idx}
                  recommendation={rec}
                  projectId={projectId}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  onGoDeeper={handleGoDeeper}
                  onComplete={handleComplete}
                  stickyNotes={stickyNotes}
                  onAddStickyNote={(content) => addStickyNote(rec.id || rec.what_to_improve, content)}
                  onUpdateStickyNote={updateStickyNote}
                  onDeleteStickyNote={deleteStickyNote}
                  canComment={canComment}
                  canEdit={canEdit}
                  isOwner={isOwner}
                  currentUserId={user?._id || ""}
                  readOnly={!canComment && !canEdit}
                />
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="declined" className="space-y-4 mt-6">
          {declinedRecs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No declined recommendations</p>
            </div>
          ) : (
            <>
              <div className="bg-secondary/50 border border-border rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground">
                  Declined recommendations stay visible so you can reconsider them later.
                </p>
              </div>
              {declinedRecs.map((rec, idx) => (
                <RecommendationCard
                  key={idx}
                  recommendation={rec}
                  projectId={projectId}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  onGoDeeper={handleGoDeeper}
                  onComplete={handleComplete}
                  stickyNotes={stickyNotes}
                  onAddStickyNote={(content) => addStickyNote(rec.id || rec.what_to_improve, content)}
                  onUpdateStickyNote={updateStickyNote}
                  onDeleteStickyNote={deleteStickyNote}
                  canComment={canComment}
                  canEdit={canEdit}
                  isOwner={isOwner}
                  currentUserId={user?._id || ""}
                  readOnly={!canComment && !canEdit}
                />
              ))}
            </>
          )}
        </TabsContent>
        </Tabs>
      )}

      {/* Dialogs */}
      {selectedRecommendation && (
        <>
          <GoDeeperDialog
            open={goDeeperOpen}
            onOpenChange={setGoDeeperOpen}
            recommendation={selectedRecommendation}
            projectId={projectId}
            onUpdate={onUpdate}
          />
          <CompleteRecommendationDialog
            open={completeDialogOpen}
            onOpenChange={setCompleteDialogOpen}
            recommendation={selectedRecommendation}
            projectId={projectId}
            onComplete={() => {
              onUpdate();
              setCompleteDialogOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
};